export const PENDING_ARTIFACT_ID_PREFIX = 'pending-';
function buildPendingArtifact(folder, input) {
    let sortOrder = 0;
    for (const a of folder.artifacts)
        sortOrder = Math.max(sortOrder, a.sortOrder + 1);
    return {
        id: input.id,
        folderId: input.folderId,
        artifactType: input.artifactType,
        name: input.name,
        color: input.color,
        displayUnit: input.displayUnit,
        geometryJson: input.geometryJson,
        sortOrder,
        createdAt: input.now,
        updatedAt: input.now,
        icon: input.icon,
        showLabel: input.showLabel,
        pending: 'creating',
    };
}
function insertPending(folders, input, afterArtifactId) {
    return folders.map(f => {
        if (f.id !== input.folderId)
            return f;
        const pending = buildPendingArtifact(f, input);
        const idx = afterArtifactId ? f.artifacts.findIndex(a => a.id === afterArtifactId) : -1;
        const insertAt = idx >= 0 ? idx + 1 : f.artifacts.length;
        return { ...f, artifacts: [...f.artifacts.slice(0, insertAt), pending, ...f.artifacts.slice(insertAt)] };
    });
}
export function appendPendingArtifact(folders, input) {
    return insertPending(folders, input, null);
}
// Lands where the real copy will; falls back to the end when the anchor is gone.
export function insertPendingArtifactAfter(folders, input, afterArtifactId) {
    return insertPending(folders, input, afterArtifactId);
}
// Swap the temp row for the server record in place. A refetch may already
// have delivered the real row; never leave two rows with the same id.
export function resolvePendingArtifact(folders, folderId, tempId, created) {
    const hasTemp = folders.some(f => f.artifacts.some(a => a.id === tempId));
    if (!hasTemp) {
        if (folders.some(f => f.artifacts.some(a => a.id === created.id)))
            return folders;
        return folders.map(f => (f.id === folderId ? { ...f, artifacts: [...f.artifacts, created] } : f));
    }
    return folders.map(f => {
        if (!f.artifacts.some(a => a.id === tempId || a.id === created.id))
            return f;
        const next = [];
        for (const a of f.artifacts) {
            if (a.id === tempId)
                next.push(created);
            else if (a.id !== created.id)
                next.push(a);
        }
        return { ...f, artifacts: next };
    });
}
export function removeArtifact(folders, artifactId) {
    return folders.map(f => (f.artifacts.some(a => a.id === artifactId)
        ? { ...f, artifacts: f.artifacts.filter(a => a.id !== artifactId) }
        : f));
}
function setDeleting(folders, artifactId, deleting) {
    return folders.map(f => {
        if (!f.artifacts.some(a => a.id === artifactId))
            return f;
        return {
            ...f,
            artifacts: f.artifacts.map(a => {
                if (a.id !== artifactId)
                    return a;
                if (deleting)
                    return { ...a, pending: 'deleting' };
                if (a.pending !== 'deleting')
                    return a;
                const settled = { ...a };
                delete settled.pending;
                return settled;
            }),
        };
    });
}
export function markArtifactDeleting(folders, artifactId) {
    return setDeleting(folders, artifactId, true);
}
export function unmarkArtifactDeleting(folders, artifactId) {
    return setDeleting(folders, artifactId, false);
}
// Ids of a folder's settled rows in display order, for the reorder call.
export function settledArtifactIds(folders, folderId) {
    const folder = folders.find(f => f.id === folderId);
    return folder ? folder.artifacts.filter(a => !a.pending).map(a => a.id) : [];
}
// In-flight creates survive a refetch, each re-placed after the nearest
// preceding row the server still returns (or at the end). In-flight deletes
// keep their marker while the server still returns the row.
export function mergeFetchedArtifacts(fetched, current) {
    const deleting = new Set();
    const currentById = new Map();
    for (const f of current) {
        currentById.set(f.id, f);
        for (const a of f.artifacts)
            if (a.pending === 'deleting')
                deleting.add(a.id);
    }
    return fetched.map(f => {
        var _a, _b, _c;
        const prevArtifacts = (_b = (_a = currentById.get(f.id)) === null || _a === void 0 ? void 0 : _a.artifacts) !== null && _b !== void 0 ? _b : [];
        const creating = prevArtifacts.filter(a => a.pending === 'creating');
        const marked = deleting.size > 0 && f.artifacts.some(a => deleting.has(a.id));
        if (creating.length === 0 && !marked)
            return f;
        const base = marked
            ? f.artifacts.map(a => (deleting.has(a.id) ? { ...a, pending: 'deleting' } : a))
            : f.artifacts;
        if (creating.length === 0)
            return { ...f, artifacts: base };
        const fetchedIds = new Set(base.map(a => a.id));
        const after = new Map();
        const tail = [];
        let anchor = null;
        for (const a of prevArtifacts) {
            if (a.pending === 'creating') {
                if (anchor)
                    after.set(anchor, [...((_c = after.get(anchor)) !== null && _c !== void 0 ? _c : []), a]);
                else
                    tail.push(a);
            }
            else if (fetchedIds.has(a.id)) {
                anchor = a.id;
            }
        }
        const next = [];
        for (const a of base) {
            next.push(a);
            const placed = after.get(a.id);
            if (placed)
                next.push(...placed);
        }
        next.push(...tail);
        return { ...f, artifacts: next };
    });
}
function withoutArtifacts(folders, drop) {
    if (!folders.some(f => f.artifacts.some(drop)))
        return folders;
    return folders.map(f => (f.artifacts.some(drop) ? { ...f, artifacts: f.artifacts.filter(a => !drop(a)) } : f));
}
// Map view: a creating row's source drawing is still on the canvas.
export function withoutCreatingArtifacts(folders) {
    return withoutArtifacts(folders, a => a.pending === 'creating');
}
// Selection view: no marked row can be selected or acted on as a group.
export function withoutPendingArtifacts(folders) {
    return withoutArtifacts(folders, a => !!a.pending);
}
